import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import Swal from 'sweetalert2';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import AuthService from '../../services/authService';
import { getImageURL } from '../../utils/api';
import {
  batchStatusBadgeClass,
  formatStatusLabel,
  STUDENT_STATUS_OPTIONS,
  deliveryStatusBadgeClass,
  deliveryStatusLabel,
} from '../../utils/batchStudentStatusHelpers';
import {
  formatDate,
  formatScheduleType,
  gradeBadgeClass,
  formatMarkValue,
  isFailingMark,
  monthsSince,
  daysSince,
  subjectCellBgClass,
  formatSubmissionDateVariance,
  formatDaysRemaining,
  daysRemainingBadgeClass,
  attendanceBadgeClass,
  groupSubjectsBySemester,
  semesterGroupBadgeClass,
} from '../../utils/productSubmissionHelpers';

function LoadingSpinner({ label, compact }) {
  return (
    <div
      className={`flex flex-col items-center justify-center gap-3 rounded-xl border border-gray-200 bg-white text-center ${
        compact ? 'p-6' : 'p-16'
      }`}
    >
      <svg className={`animate-spin text-emerald-600 ${compact ? 'h-6 w-6' : 'h-9 w-9'}`} viewBox="0 0 24 24" fill="none">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
      <p className="text-sm font-medium text-gray-500">{label}</p>
    </div>
  );
}

function StudentAvatar({ imagePath, name }) {
  const [errored, setErrored] = useState(false);
  if (!imagePath || errored) {
    return (
      <div
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-200 text-gray-400 ring-1 ring-gray-200"
        title={name || 'Student'}
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 12c2.7 0 4.9-2.2 4.9-4.9S14.7 2.2 12 2.2 7.1 4.4 7.1 7.1 9.3 12 12 12zm0 2.5c-3.3 0-9.9 1.6-9.9 4.9v2.4h19.8v-2.4c0-3.3-6.6-4.9-9.9-4.9z" />
        </svg>
      </div>
    );
  }
  return (
    <img
      src={getImageURL(imagePath)}
      alt={name || 'Student'}
      onError={() => setErrored(true)}
      className="h-8 w-8 shrink-0 rounded-full object-cover ring-1 ring-gray-200"
    />
  );
}

const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? '#059669' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(5,150,105,0.2)' : 'none',
    '&:hover': { borderColor: '#059669' },
    borderRadius: '0.5rem', minHeight: '42px', fontSize: '0.875rem',
  }),
  placeholder: (base) => ({ ...base, color: '#9ca3af', fontSize: '0.875rem' }),
  menu: (base) => ({ ...base, borderRadius: '0.5rem', boxShadow: '0 10px 25px rgba(0,0,0,0.1)', border: '1px solid #e5e7eb', zIndex: 20 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#d1fae5' : state.isFocused ? '#ecfdf5' : 'white',
    color: state.isSelected ? '#065f46' : '#1f2937', cursor: 'pointer',
  }),
  multiValue: (base) => ({ ...base, backgroundColor: '#d1fae5' }),
  multiValueLabel: (base) => ({ ...base, color: '#065f46' }),
};

const BATCH_STATUS_TABS = [
  { value: 'ongoing', label: 'Ongoing' },
  { value: 'not started', label: 'Not Started' },
  { value: 'delivery completed', label: 'Delivery Completed' },
  { value: 'batch completed', label: 'Batch Completed' },
  { value: 'all', label: 'All' },
];

const EXPECTED_MODAL_TABS = [
  { value: 'batch', label: 'Batch / Course' },
  { value: 'subject', label: 'Subject Default' },
];

function ExpectedClassesModal({ subject, saving, onClose, onSave }) {
  const [activeTab, setActiveTab] = useState('batch');
  const [batchValue, setBatchValue] = useState(String(subject.numberOfClasses ?? 0));
  const [batchScope, setBatchScope] = useState('batch');
  const [subjectValue, setSubjectValue] = useState(String(subject.subjectDefaultClasses ?? 0));

  const submit = () => {
    if (activeTab === 'subject') {
      const num = Number(subjectValue);
      if (!Number.isFinite(num) || num < 0) {
        Swal.fire('Invalid value', 'Please enter a non-negative number.', 'warning');
        return;
      }
      onSave(num, 'subject');
      return;
    }
    const num = Number(batchValue);
    if (!Number.isFinite(num) || num < 0) {
      Swal.fire('Invalid value', 'Please enter a non-negative number.', 'warning');
      return;
    }
    onSave(num, batchScope);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h4 className="text-sm font-semibold text-gray-800">Change expected classes</h4>
        <p className="mt-0.5 truncate text-xs text-gray-500">{subject.subjectShortName || subject.subjectName}</p>

        <div className="mt-3 flex items-center gap-1 rounded-lg bg-gray-100 p-1">
          {EXPECTED_MODAL_TABS.map((tab) => (
            <button
              key={tab.value}
              type="button"
              onClick={() => setActiveTab(tab.value)}
              className={`flex-1 rounded-md px-2 py-1.5 text-xs font-semibold transition-colors ${
                activeTab === tab.value ? 'bg-white text-emerald-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'batch' ? (
          <>
            <label className="mt-4 block text-xs font-medium text-gray-700">Number of classes</label>
            <input
              type="number"
              min={0}
              value={batchValue}
              onChange={(e) => setBatchValue(e.target.value)}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
            />
            {Number.isFinite(Number(batchValue)) && batchValue !== '' && (
              <p className="mt-1 text-xs font-semibold text-emerald-700">= {Number(batchValue) * 2}h total hours</p>
            )}
            <p className="mt-1 text-[11px] text-gray-400">
              {subject.batchOverrideClasses !== null
                ? `Currently overridden for this batch (subject default: ${subject.subjectDefaultClasses}).`
                : `Currently using the subject's default value (${subject.subjectDefaultClasses}).`}
            </p>

            <div className="mt-4 space-y-2">
              <label
                className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
                  batchScope === 'batch' ? 'border-emerald-500 bg-emerald-50 text-emerald-800' : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <input type="radio" name="expected-scope" checked={batchScope === 'batch'} onChange={() => setBatchScope('batch')} className="h-3.5 w-3.5" />
                Only this batch
              </label>
              <label
                className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
                  batchScope === 'all' ? 'border-emerald-500 bg-emerald-50 text-emerald-800' : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <input type="radio" name="expected-scope" checked={batchScope === 'all'} onChange={() => setBatchScope('all')} className="h-3.5 w-3.5" />
                All batches of this course delivering this subject
              </label>
            </div>
          </>
        ) : (
          <>
            <label className="mt-4 block text-xs font-medium text-gray-700">Subject's default number of classes</label>
            <input
              type="number"
              min={0}
              value={subjectValue}
              onChange={(e) => setSubjectValue(e.target.value)}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
            />
            {Number.isFinite(Number(subjectValue)) && subjectValue !== '' && (
              <p className="mt-1 text-xs font-semibold text-emerald-700">= {Number(subjectValue) * 2}h total hours</p>
            )}
            <p className="mt-1 text-[11px] text-gray-400">
              Changes the subject's own default (<code>subjects.numberOfClasses</code>) system-wide — every batch that
              doesn't already have its own batch-specific override will pick up this new value.
            </p>
          </>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function toDateInputValue(value) {
  return value || '';
}

// Shifts the planned release/late-submission day gaps onto a new actual
// submission date, so "3 days before submission" / "7 days after submission"
// stays true even when the actual date differs from the plan.
function computeAutoActualDates(subject, actualSubmissionDate) {
  if (!actualSubmissionDate) return { release: '', late: '' };
  const submission = new Date(actualSubmissionDate);
  if (Number.isNaN(submission.getTime())) return { release: '', late: '' };

  let release = '';
  let late = '';

  if (subject.plannedReleaseDate && subject.plannedSubmissionDate) {
    const plannedRelease = new Date(subject.plannedReleaseDate);
    const plannedSubmission = new Date(subject.plannedSubmissionDate);
    if (!Number.isNaN(plannedRelease.getTime()) && !Number.isNaN(plannedSubmission.getTime())) {
      const gapDays = Math.round((plannedSubmission.getTime() - plannedRelease.getTime()) / 86400000);
      const releaseDate = new Date(submission);
      releaseDate.setDate(releaseDate.getDate() - gapDays);
      release = releaseDate.toISOString().slice(0, 10);
    }
  }

  if (subject.plannedLateSubmissionDate && subject.plannedSubmissionDate) {
    const plannedLate = new Date(subject.plannedLateSubmissionDate);
    const plannedSubmission = new Date(subject.plannedSubmissionDate);
    if (!Number.isNaN(plannedLate.getTime()) && !Number.isNaN(plannedSubmission.getTime())) {
      const gapDays = Math.round((plannedLate.getTime() - plannedSubmission.getTime()) / 86400000);
      const lateDate = new Date(submission);
      lateDate.setDate(lateDate.getDate() + gapDays);
      late = lateDate.toISOString().slice(0, 10);
    }
  }

  return { release, late };
}

function ActualDatesModal({ subject, saving, onClose, onSave }) {
  const [releaseDate, setReleaseDate] = useState(toDateInputValue(subject.actualReleaseDate));
  const [submissionDate, setSubmissionDate] = useState(toDateInputValue(subject.actualSubmissionDate));
  const [lateDate, setLateDate] = useState(toDateInputValue(subject.actualLateSubmissionDate));
  const [groupBatches, setGroupBatches] = useState([]);
  const [groupLoading, setGroupLoading] = useState(true);
  const [applyToGroup, setApplyToGroup] = useState(true);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      setGroupLoading(true);
      try {
        const resp = await axiosInstance.get(`/admin/dashboard/product-submission-report/batch-group/${subject.batchSubjectId}`);
        if (!isMounted) return;
        setGroupBatches(Array.isArray(resp?.data?.batches) ? resp.data.batches : []);
      } catch (err) {
        console.error('Failed to load batch group info', err);
      } finally {
        if (isMounted) setGroupLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [subject.batchSubjectId]);

  const handleSubmissionDateChange = (value) => {
    setSubmissionDate(value);
    const { release, late } = computeAutoActualDates(subject, value);
    if (release) setReleaseDate(release);
    if (late) setLateDate(late);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
          <p className="text-sm font-semibold text-emerald-800">Actual assignment date management</p>
          <p className="mt-0.5 text-xs text-emerald-700">Update when assignments were actually released and submitted.</p>
        </div>

        {!groupLoading && groupBatches.length > 1 && (
          <div className="mt-3 rounded-xl border border-blue-200 bg-blue-50 p-3">
            <label className="flex items-start gap-2 text-xs font-semibold text-blue-900">
              <input
                type="checkbox"
                checked={applyToGroup}
                onChange={(e) => setApplyToGroup(e.target.checked)}
                className="mt-0.5 h-3.5 w-3.5"
              />
              Apply to all batches sharing this class ({groupBatches.length})
            </label>
            <div className="mt-1.5 flex flex-wrap gap-1 pl-5">
              {groupBatches.map((b) => (
                <span key={b.batchSubjectId} className="rounded-full bg-white px-2 py-0.5 text-[10px] font-medium text-blue-700 ring-1 ring-blue-200">
                  {b.batchLabel}
                </span>
              ))}
            </div>
            {!applyToGroup && (
              <p className="mt-1.5 pl-5 text-[11px] text-blue-700">Unchecked — only this batch will be updated.</p>
            )}
          </div>
        )}

        <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
          <h4 className="text-sm font-bold text-emerald-900">Actual Assignment Dates</h4>

          <label className="mt-3 block text-xs font-medium text-gray-700">Actual Release Date</label>
          <input
            type="date"
            value={releaseDate}
            onChange={(e) => setReleaseDate(e.target.value)}
            className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
          />

          <label className="mt-3 block text-xs font-medium text-gray-700">Actual Submission Date</label>
          <input
            type="date"
            value={submissionDate}
            onChange={(e) => handleSubmissionDateChange(e.target.value)}
            className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
          />

          <label className="mt-3 block text-xs font-medium text-gray-700">Actual Late Submission Date</label>
          <input
            type="date"
            value={lateDate}
            onChange={(e) => setLateDate(e.target.value)}
            className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
          />
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSave({ releaseDate, submissionDate, lateDate, applyToGroup: groupBatches.length > 1 ? applyToGroup : false })}
            disabled={saving}
            className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Update Dates'}
          </button>
        </div>
      </div>
    </div>
  );
}

const SUBJECT_STATUS_OPTIONS = [
  { value: 'not started', label: 'Not Started' },
  { value: 'ongoing', label: 'Ongoing' },
  { value: 'completed', label: 'Completed' },
];

function SubjectStatusModal({ subject, saving, onClose, onSave }) {
  const [status, setStatus] = useState(subject.status || 'not started');
  const [groupBatches, setGroupBatches] = useState([]);
  const [groupLoading, setGroupLoading] = useState(true);
  const [applyToGroup, setApplyToGroup] = useState(true);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      setGroupLoading(true);
      try {
        const resp = await axiosInstance.get(`/admin/dashboard/product-submission-report/batch-group/${subject.batchSubjectId}`);
        if (!isMounted) return;
        setGroupBatches(Array.isArray(resp?.data?.batches) ? resp.data.batches : []);
      } catch (err) {
        console.error('Failed to load batch group info', err);
      } finally {
        if (isMounted) setGroupLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [subject.batchSubjectId]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h4 className="text-sm font-semibold text-gray-800">Change subject status</h4>
        <p className="mt-0.5 truncate text-xs text-gray-500">{subject.subjectShortName || subject.subjectName}</p>

        {!groupLoading && groupBatches.length > 1 && (
          <div className="mt-3 rounded-xl border border-blue-200 bg-blue-50 p-3">
            <label className="flex items-start gap-2 text-xs font-semibold text-blue-900">
              <input
                type="checkbox"
                checked={applyToGroup}
                onChange={(e) => setApplyToGroup(e.target.checked)}
                className="mt-0.5 h-3.5 w-3.5"
              />
              Apply to all batches sharing this class ({groupBatches.length})
            </label>
            <div className="mt-1.5 flex flex-wrap gap-1 pl-5">
              {groupBatches.map((b) => (
                <span key={b.batchSubjectId} className="rounded-full bg-white px-2 py-0.5 text-[10px] font-medium text-blue-700 ring-1 ring-blue-200">
                  {b.batchLabel}
                </span>
              ))}
            </div>
            {!applyToGroup && (
              <p className="mt-1.5 pl-5 text-[11px] text-blue-700">Unchecked — only this batch will be updated.</p>
            )}
          </div>
        )}

        <div className="mt-4 space-y-2">
          {SUBJECT_STATUS_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
                status === opt.value ? 'border-emerald-500 bg-emerald-50 text-emerald-800' : 'border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <input type="radio" name="subject-status" checked={status === opt.value} onChange={() => setStatus(opt.value)} className="h-3.5 w-3.5" />
              {opt.label}
            </label>
          ))}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSave({ status, applyToGroup: groupBatches.length > 1 ? applyToGroup : false })}
            disabled={saving}
            className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function BatchMarkSheetTable({ batch, markType, onStatusChange, onRefetch }) {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkValue, setBulkValue] = useState('ongoing');
  const [bulkSaving, setBulkSaving] = useState(false);
  const [expectedModalSubject, setExpectedModalSubject] = useState(null);
  const [expectedSaving, setExpectedSaving] = useState(false);
  const [actualDatesSubject, setActualDatesSubject] = useState(null);
  const [actualDatesSaving, setActualDatesSaving] = useState(false);
  const [statusModalSubject, setStatusModalSubject] = useState(null);
  const [statusModalSaving, setStatusModalSaving] = useState(false);

  const semesterGroups = useMemo(() => groupSubjectsBySemester(batch.subjects), [batch.subjects]);

  const saveActualDates = async ({ releaseDate, submissionDate, lateDate, applyToGroup }) => {
    setActualDatesSaving(true);
    try {
      await axiosInstance.put('/admin/dashboard/product-submission-report/actual-dates', {
        batchSubjectId: actualDatesSubject.batchSubjectId,
        actualAssignmentReleaseDate: releaseDate || null,
        actualAssignmentSubmissionDate: submissionDate || null,
        actualAssignmentLateSubmissionDate: lateDate || null,
        applyToGroup,
      });
      setActualDatesSubject(null);
      onRefetch();
    } catch (err) {
      Swal.fire('Update failed', err?.response?.data?.message || 'Failed to update actual assignment dates', 'error');
    } finally {
      setActualDatesSaving(false);
    }
  };

  const saveSubjectStatus = async ({ status, applyToGroup }) => {
    setStatusModalSaving(true);
    try {
      await axiosInstance.put('/admin/dashboard/product-submission-report/subject-status', {
        batchSubjectId: statusModalSubject.batchSubjectId,
        status,
        applyToGroup,
      });
      setStatusModalSubject(null);
      onRefetch();
    } catch (err) {
      Swal.fire('Update failed', err?.response?.data?.message || 'Failed to update subject status', 'error');
    } finally {
      setStatusModalSaving(false);
    }
  };

  const saveExpectedClasses = async (numberOfClasses, scope) => {
    setExpectedSaving(true);
    try {
      await axiosInstance.put('/admin/dashboard/product-submission-report/expected-classes', {
        batchSubjectId: expectedModalSubject.batchSubjectId,
        numberOfClasses,
        scope,
      });
      setExpectedModalSubject(null);
      onRefetch();
    } catch (err) {
      Swal.fire('Update failed', err?.response?.data?.message || 'Failed to update expected classes', 'error');
    } finally {
      setExpectedSaving(false);
    }
  };

  const allSelected = batch.students.length > 0 && selectedIds.size === batch.students.length;

  const toggleAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(batch.students.map((s) => s.registrationId)));
  };

  const toggleOne = (registrationId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(registrationId)) next.delete(registrationId);
      else next.add(registrationId);
      return next;
    });
  };

  const applyBulkStatus = async () => {
    if (selectedIds.size === 0) return;
    setBulkSaving(true);
    const ids = Array.from(selectedIds);
    let success = 0;
    let failure = 0;
    for (const registrationId of ids) {
      try {
        await axiosInstance.put(`/admin/registrations/${registrationId}`, {
          deliveryStatus: bulkValue === 'ongoing' ? '' : bulkValue,
        });
        onStatusChange(batch.batchId, registrationId, bulkValue === 'ongoing' ? null : bulkValue);
        success += 1;
      } catch (err) {
        failure += 1;
      }
    }
    setBulkSaving(false);
    setSelectedIds(new Set());
    Swal.fire({
      icon: failure ? 'warning' : 'success',
      title: 'Bulk update finished',
      text: `${success} student${success === 1 ? '' : 's'} updated${failure ? `, ${failure} failed` : ''}.`,
      confirmButtonColor: '#059669',
    });
  };

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="flex items-center justify-between gap-3 border-b border-gray-100 bg-gray-50 px-5 py-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="truncate text-sm font-semibold text-gray-800">
              {batch.batchInternalName || batch.whatsappGroupName || batch.batchName || 'Batch'}
            </p>
            <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${batchStatusBadgeClass(batch.status)}`}>
              {formatStatusLabel(batch.status)}
            </span>
            {batch.status === 'ongoing' && formatDaysRemaining(batch.batchEndingDate) && (
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${daysRemainingBadgeClass(batch.batchEndingDate)}`}>
                {formatDaysRemaining(batch.batchEndingDate)}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500">
            {formatDate(batch.batchStartingDate)} – {formatDate(batch.batchEndingDate)}
          </p>
        </div>
        <span className="shrink-0 rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-700">
          {batch.students.length} student{batch.students.length === 1 ? '' : 's'}
        </span>
      </div>

      {selectedIds.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 border-b border-amber-100 bg-amber-50 px-5 py-2.5">
          <span className="text-xs font-semibold text-amber-800">{selectedIds.size} selected</span>
          <select
            value={bulkValue}
            onChange={(e) => setBulkValue(e.target.value)}
            className="rounded-md border border-amber-300 bg-white px-2 py-1 text-xs focus:border-amber-500 focus:outline-none"
          >
            {STUDENT_STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={applyBulkStatus}
            disabled={bulkSaving}
            className="rounded-md bg-amber-600 px-3 py-1 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
          >
            {bulkSaving ? 'Applying…' : `Apply to ${selectedIds.size}`}
          </button>
          <button
            type="button"
            onClick={() => setSelectedIds(new Set())}
            className="rounded-md border border-amber-300 bg-white px-3 py-1 text-xs font-medium text-amber-700 hover:bg-amber-100"
          >
            Clear
          </button>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-left" style={{ minWidth: `${880 + batch.subjects.length * 132}px` }}>
          <thead>
            <tr className="border-b border-gray-100 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
              <th colSpan={5} className="border border-gray-200 bg-gray-50 px-3 py-1.5"></th>
              {semesterGroups.map((group) => (
                <th
                  key={group.semester ?? 'none'}
                  colSpan={group.subjects.length}
                  className={`border border-gray-200 px-2 py-1.5 text-center ${semesterGroupBadgeClass(group)}`}
                >
                  Semester {group.semester ?? '—'}
                  <div className="mt-0.5 text-[9px] font-normal normal-case opacity-75">
                    {formatDate(group.startDate)} – {formatDate(group.endDate)}
                  </div>
                </th>
              ))}
            </tr>
            <tr className="border-b border-gray-100 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
              <th className="border border-gray-200 px-3 py-2.5">
                <input type="checkbox" checked={allSelected} onChange={toggleAll} className="h-3.5 w-3.5" />
              </th>
              <th className="border border-gray-200 px-4 py-2.5">University ID</th>
              <th className="border border-gray-200 px-4 py-2.5">Student ID</th>
              <th className="border border-gray-200 px-4 py-2.5">Registration No.</th>
              <th className="border border-gray-200 px-4 py-2.5">Name</th>
              {batch.subjects.map((subj) => {
                const overScheduled = subj.status !== 'completed'
                  && typeof subj.scheduledHours === 'number'
                  && subj.scheduledHours > (subj.numberOfClasses || 0) * 2;
                const deadlinePassed = !!subj.actualSubmissionDate && new Date(subj.actualSubmissionDate) < new Date();
                // Mutually exclusive, single class-set per branch — avoids any
                // Tailwind utility-order conflict between the plain header
                // border and the over-scheduled/deadline-passed highlights.
                let columnHighlight = 'border border-gray-200';
                if (overScheduled) columnHighlight = 'border-2 border-red-500 bg-red-50';
                else if (deadlinePassed) columnHighlight = 'border-2 border-green-500 bg-green-50';
                const canEditActualDates = subj.status === 'completed' && !subj.actualSubmissionDate;
                const canEditStatus = subj.status !== 'completed';
                const handleBadgeDoubleClick = () => {
                  if (canEditActualDates) setActualDatesSubject(subj);
                  else if (canEditStatus) setStatusModalSubject(subj);
                };
                return (
                <th
                  key={subj.batchSubjectId}
                  className={`min-w-[128px] px-1.5 py-2 text-center align-top ${columnHighlight}`}
                  title={subj.subjectName}
                >
                  <div
                    onDoubleClick={handleBadgeDoubleClick}
                    title={
                      canEditActualDates
                        ? 'Double-click to set actual assignment dates'
                        : canEditStatus
                          ? 'Double-click to change subject status'
                          : subj.subjectName
                    }
                    className={`mx-auto mb-1 inline-block rounded-md bg-gradient-to-r from-gray-800 to-gray-900 px-2 py-0.5 text-[10px] font-bold normal-case tracking-wide text-white ${
                      canEditActualDates ? 'cursor-pointer ring-2 ring-emerald-400' : canEditStatus ? 'cursor-pointer ring-2 ring-blue-400' : ''
                    }`}
                  >
                    {subj.subjectShortName || subj.subjectName}
                  </div>

                  <div className="text-[9px] font-normal normal-case text-gray-400">{formatDate(subj.plannedSubmissionDate)}</div>
                  {subj.actualSubmissionDate && (
                    <div className="mt-0.5 inline-block rounded-full bg-green-100 px-1.5 py-0.5 text-[9px] font-semibold normal-case text-green-700">
                      {formatDate(subj.actualSubmissionDate)}
                    </div>
                  )}
                  {formatSubmissionDateVariance(subj) && (
                    <div className="mt-0.5 text-[9px] font-semibold normal-case text-amber-600">{formatSubmissionDateVariance(subj)}</div>
                  )}

                  <div className="mt-1 flex items-center justify-center gap-1 truncate text-[9px] font-medium normal-case text-gray-600" title={subj.lecturerName || ''}>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-2.5 w-2.5 shrink-0 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 12c2.7 0 4.9-2.2 4.9-4.9S14.7 2.2 12 2.2 7.1 4.4 7.1 7.1 9.3 12 12 12zm0 2.5c-3.3 0-9.9 1.6-9.9 4.9v2.4h19.8v-2.4c0-3.3-6.6-4.9-9.9-4.9z" />
                    </svg>
                    <span className="truncate">{subj.lecturerName || 'Not assigned'}</span>
                  </div>

                  <div className="mt-1.5 grid grid-cols-3 divide-x divide-gray-200 overflow-hidden rounded-md border border-gray-200 bg-gray-50">
                    <div
                      className="cursor-pointer px-1 py-1 hover:bg-cyan-50"
                      onDoubleClick={() => setExpectedModalSubject(subj)}
                      title="Double-click to change expected classes"
                    >
                      <div className="text-[8px] font-bold uppercase tracking-wide text-cyan-600">Expected</div>
                      <div className="text-[10px] font-bold normal-case text-gray-800">{subj.numberOfClasses != null ? `${subj.numberOfClasses * 2}h` : '—'}</div>
                      <div className="text-[8px] normal-case text-gray-400">{subj.numberOfClasses ?? '—'} cls</div>
                    </div>
                    <div className="px-1 py-1">
                      <div className={`text-[8px] font-bold uppercase tracking-wide ${overScheduled ? 'text-rose-600' : 'text-violet-600'}`}>Sched.</div>
                      <div className="text-[10px] font-bold normal-case text-gray-800">{typeof subj.scheduledHours === 'number' ? `${subj.scheduledHours}h` : '—'}</div>
                      <div className="text-[8px] normal-case text-gray-400">{subj.scheduledClasses ?? '—'} cls</div>
                    </div>
                    <div className="px-1 py-1">
                      <div className="text-[8px] font-bold uppercase tracking-wide text-amber-600">Pending</div>
                      <div className="text-[10px] font-bold normal-case text-gray-800">{typeof subj.pendingHours === 'number' ? `${subj.pendingHours}h` : '—'}</div>
                      <div className="text-[8px] normal-case text-gray-400">{subj.pendingClasses ?? '—'} cls</div>
                    </div>
                  </div>
                </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {batch.students.map((s) => {
              const monthsPassed = s.universityRegisterDate ? monthsSince(s.universityRegisterDate) : null;
              return (
              <tr key={`${s.studentId}-${s.registrationNumber}`} className="hover:bg-gray-50">
                <td className="border border-gray-200 px-3 py-2.5">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(s.registrationId)}
                    onChange={() => toggleOne(s.registrationId)}
                    className="h-3.5 w-3.5"
                  />
                </td>
                <td className="border border-gray-200 px-4 py-2.5 text-xs">
                  <div className="font-medium text-gray-700">{s.universityID || '—'}</div>
                  {s.universityRegisterDate && (
                    <>
                      <div className="mt-0.5 text-gray-400">{formatDate(s.universityRegisterDate)}</div>
                      <div className="text-gray-400">{monthsPassed} mo{monthsPassed === 1 ? '' : 's'} ago</div>
                    </>
                  )}
                </td>
                <td className="border border-gray-200 px-4 py-2.5 text-xs text-gray-600">{s.studentId || '—'}</td>
                <td className="border border-gray-200 px-4 py-2.5 text-xs text-gray-600">{s.registrationNumber || '—'}</td>
                <td className="border border-gray-200 px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <StudentAvatar imagePath={s.imagePath} name={s.studentName} />
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-gray-800">{s.studentName || '—'}</div>
                      <span className={`mt-0.5 inline-block rounded-full px-2 py-0.5 text-[10px] font-bold ${deliveryStatusBadgeClass(s.deliveryStatus)}`}>
                        {deliveryStatusLabel(s.deliveryStatus)}
                      </span>
                      <div className="mt-0.5 flex items-center gap-1">
                        <span className={`inline-block rounded-full px-1.5 py-0.5 text-[9px] font-bold ${attendanceBadgeClass(s.attendancePercentage)}`}>
                          {s.attendancePercentage === null || s.attendancePercentage === undefined ? '—' : `${s.attendancePercentage}%`} att.
                        </span>
                      </div>
                      <div className="mt-0.5 flex items-center gap-1 text-[9px] text-gray-400">
                        <span>Last present: {formatDate(s.lastPresentDate)}</span>
                        {s.lastPresentDate && (
                          <span className="inline-block rounded-full bg-gray-100 px-1.5 py-0.5 text-[9px] font-semibold text-gray-500">
                            {daysSince(s.lastPresentDate)}d ago
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </td>
                {batch.subjects.map((subj) => {
                  const mark = s.marks ? s.marks[subj.batchSubjectId] : null;
                  const rawSubmissionStatus = s.submissionStatuses ? s.submissionStatuses[subj.batchSubjectId] : null;
                  // Only treat "not submitted" as NS once the actual submission
                  // deadline has already passed — before that, it's just not due yet.
                  const deadlinePassed = !!subj.actualSubmissionDate && new Date(subj.actualSubmissionDate) < new Date();
                  const submissionStatus = deadlinePassed ? rawSubmissionStatus : null;
                  const overScheduled = subj.status !== 'completed'
                    && typeof subj.scheduledHours === 'number'
                    && subj.scheduledHours > (subj.numberOfClasses || 0) * 2;
                  const failing = isFailingMark(mark, markType);
                  // Mutually exclusive, single class-set per branch — avoids
                  // any Tailwind utility-order conflict between the plain
                  // body-cell border and the highlighted-column borders.
                  let cellBorder = 'border border-gray-200';
                  if (overScheduled) cellBorder = 'border-2 border-red-500';
                  else if (deadlinePassed) cellBorder = 'border-2 border-green-500';
                  return (
                    <td
                      key={subj.batchSubjectId}
                      className={`${cellBorder} px-2 py-2.5 text-center ${failing ? 'bg-red-50' : subjectCellBgClass(subj)}`}
                    >
                      {markType === 'grade' ? (
                        <span className={`inline-block rounded px-2 py-0.5 text-xs font-bold ${gradeBadgeClass(mark)}`}>
                          {formatMarkValue(mark, markType, submissionStatus)}
                        </span>
                      ) : (
                        <span className={`inline-block rounded px-2 py-0.5 text-xs font-bold ${failing ? 'bg-red-100 text-red-700' : 'text-gray-700'}`}>
                          {formatMarkValue(mark, markType, submissionStatus)}
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {expectedModalSubject && (
        <ExpectedClassesModal
          subject={expectedModalSubject}
          saving={expectedSaving}
          onClose={() => setExpectedModalSubject(null)}
          onSave={saveExpectedClasses}
        />
      )}

      {actualDatesSubject && (
        <ActualDatesModal
          subject={actualDatesSubject}
          saving={actualDatesSaving}
          onClose={() => setActualDatesSubject(null)}
          onSave={saveActualDates}
        />
      )}

      {statusModalSubject && (
        <SubjectStatusModal
          subject={statusModalSubject}
          saving={statusModalSaving}
          onClose={() => setStatusModalSubject(null)}
          onSave={saveSubjectStatus}
        />
      )}
    </div>
  );
}

export default function ProductSubmissionReport() {
  const navigate = useNavigate();

  const [accessibleCourseIds, setAccessibleCourseIds] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [accessLoaded, setAccessLoaded] = useState(false);

  const [courseOptions, setCourseOptions] = useState([]);
  const [courseOptionsLoading, setCourseOptionsLoading] = useState(true);
  const [selectedCourses, setSelectedCourses] = useState([]); // [] = all accessible courses
  const [selectedStatus, setSelectedStatus] = useState('ongoing');
  const [search, setSearch] = useState('');

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // "Management" staff get the same unrestricted report access as Super Admin
  // (see RequireReportAccess in App.js) — without this, they'd otherwise be
  // narrowed down to their own (typically empty) coordinate_courses list.
  const isManagement = String(AuthService.getUser()?.superAdmin?.staff?.staffType || '').trim().toLowerCase() === 'management';

  const accessibleParam = useCallback(() => {
    if (isSuperAdmin || isManagement) return 'all';
    return (Array.isArray(accessibleCourseIds) ? accessibleCourseIds : [])
      .map((id) => Number(id))
      .filter((num) => Number.isFinite(num) && num > 0);
  }, [isSuperAdmin, isManagement, accessibleCourseIds]);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const accessRes = await axiosInstance.get('/admin/me/coordinate-courses').catch(() => ({ data: {} }));
        if (!isMounted) return;
        const data = accessRes.data || {};
        setAccessibleCourseIds(Array.isArray(data.accessibleCourseIds) ? data.accessibleCourseIds : []);
        setIsSuperAdmin(!!data.isSuperAdmin);
      } catch (e) {
        console.error('Failed to load coordinate courses', e);
      } finally {
        if (isMounted) setAccessLoaded(true);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  // Derive the course filter's option list across every batch status (not
  // just the currently selected tab) so switching tabs never hides a course
  // from the filter that still has batches under a different status.
  useEffect(() => {
    if (!accessLoaded) return;
    let isMounted = true;
    (async () => {
      setCourseOptionsLoading(true);
      try {
        const resp = await axiosInstance.get('/admin/dashboard/product-submission-report', {
          params: { accessibleCourses: accessibleParam(), courseIds: 'all', batchStatus: 'all' },
        });
        if (!isMounted) return;
        const list = Array.isArray(resp?.data?.courses) ? resp.data.courses : [];
        const options = list
          .filter((c) => c.courseId)
          .map((c) => {
            const baseName = c.courseShortName || c.courseName || `Course #${c.courseId}`;
            const scheduleLabel = formatScheduleType(c.scheduleType);
            return {
              value: c.courseId,
              label: scheduleLabel ? `${baseName} (${scheduleLabel})` : baseName,
            };
          })
          .sort((a, b) => a.label.localeCompare(b.label));
        setCourseOptions(options);
      } catch (e) {
        console.error('Failed to load course options', e);
      } finally {
        if (isMounted) setCourseOptionsLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [accessLoaded, accessibleParam]);

  const loadReport = useCallback(async () => {
    if (!accessLoaded) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await axiosInstance.get('/admin/dashboard/product-submission-report', {
        params: {
          accessibleCourses: accessibleParam(),
          courseIds: selectedCourses.length > 0 ? selectedCourses.map((o) => o.value).join(',') : 'all',
          batchStatus: selectedStatus,
        },
      });
      const data = resp?.data || {};
      setCourses(data.courses || []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load product submission report');
    } finally {
      setLoading(false);
    }
  }, [accessLoaded, accessibleParam, selectedCourses, selectedStatus]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const filteredCourses = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return courses;
    return courses
      .map((course) => ({
        ...course,
        batches: course.batches
          .map((batch) => ({
            ...batch,
            students: batch.students.filter((s) => {
              const combined = [s.studentName, s.studentId, s.registrationNumber, s.universityID].filter(Boolean).join(' ').toLowerCase();
              return combined.includes(term)
                || (batch.batchInternalName || '').toLowerCase().includes(term)
                || (batch.batchName || '').toLowerCase().includes(term)
                || (course.courseName || '').toLowerCase().includes(term);
            }),
          }))
          .filter((batch) => batch.students.length > 0
            || (batch.batchInternalName || '').toLowerCase().includes(term)
            || (batch.batchName || '').toLowerCase().includes(term)),
      }))
      .filter((course) => course.batches.length > 0);
  }, [courses, search]);

  const visibleCounts = useMemo(() => {
    let batches = 0;
    let students = 0;
    filteredCourses.forEach((course) => {
      batches += course.batches.length;
      course.batches.forEach((batch) => {
        students += batch.students.length;
      });
    });
    return { batches, students };
  }, [filteredCourses]);

  // Reflects a successful deliveryStatus update (single or bulk) back into
  // local state so the badge/select updates immediately without a full reload.
  const handleStatusChange = useCallback((batchId, registrationId, newStatus) => {
    setCourses((prev) => prev.map((course) => ({
      ...course,
      batches: course.batches.map((batch) => {
        if (batch.batchId !== batchId) return batch;
        return {
          ...batch,
          students: batch.students.map((s) => (
            s.registrationId === registrationId ? { ...s, deliveryStatus: newStatus } : s
          )),
        };
      }),
    })));
  }, []);

  const handlePrint = () => {
    const params = new URLSearchParams();
    params.set('courseIds', selectedCourses.length > 0 ? selectedCourses.map((o) => o.value).join(',') : 'all');
    params.set('batchStatus', selectedStatus);
    window.open(`/admin/report/product-submission/print?${params.toString()}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <DashboardLayout title="Higher End Product Submission Report">
      <div>
        <div className="mb-4 flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate('/admin/report/monitor')}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Monitor Report
          </button>
          <button
            type="button"
            onClick={handlePrint}
            className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-600 bg-emerald-600 px-3 py-2 text-xs font-medium text-white hover:bg-emerald-700"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4H7v4a2 2 0 002 2z" />
            </svg>
            Print PDF
          </button>
        </div>

        <div className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50 p-6">
          <h2 className="flex items-center gap-2 text-base font-semibold text-emerald-800">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Higher End Product Submission Report
          </h2>
          <p className="mt-1 text-sm text-emerald-900">
            Course-wise, batch-wise submission mark sheet — each student's University ID and mark per subject. HND
            courses show D/M/P/R grades; every other course shows a numeric score. English/IT certificate courses
            are excluded.
          </p>

          <div className="mt-4 flex flex-wrap gap-2">
            {BATCH_STATUS_TABS.map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => setSelectedStatus(tab.value)}
                className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
                  selectedStatus === tab.value
                    ? 'bg-emerald-600 text-white shadow-sm'
                    : 'bg-white text-gray-600 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="mt-4 flex flex-wrap items-end gap-4">
            <div className="min-w-[240px] flex-1">
              <label className="mb-1 block text-xs font-medium text-gray-700">Courses</label>
              <Select
                isMulti
                isLoading={courseOptionsLoading}
                options={courseOptions}
                value={selectedCourses}
                onChange={(opts) => setSelectedCourses(opts || [])}
                placeholder="All accessible courses (default)…"
                styles={selectStyles}
                closeMenuOnSelect={false}
              />
            </div>
            <div className="flex flex-col">
              <label className="mb-1 text-xs font-medium text-gray-700">Search</label>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Student / University ID / Batch / Course"
                className="w-64 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
              />
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">{error}</div>
        )}

        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700">
            {visibleCounts.batches} batch{visibleCounts.batches === 1 ? '' : 'es'} &middot; {visibleCounts.students} student{visibleCounts.students === 1 ? '' : 's'}
          </h3>
        </div>

        {loading ? (
          <LoadingSpinner label="Loading product submission report…" />
        ) : filteredCourses.length === 0 ? (
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-8 text-center text-gray-600">
            No batches match the current filters.
          </div>
        ) : (
          <div className="space-y-10">
            {filteredCourses.map((course) => (
              <div key={course.courseId ?? course.courseName}>
                <h3 className="mb-3 text-lg font-bold text-gray-900">
                  {course.courseShortName || course.courseName || 'Unknown Course'}
                  {formatScheduleType(course.scheduleType) && ` (${formatScheduleType(course.scheduleType)})`}
                  <span className="ml-2 text-xs font-normal text-gray-400">
                    {course.markType === 'grade' ? '· Grade: D / M / P / R' : '· Marks: numeric'}
                  </span>
                </h3>

                <div className="space-y-5">
                  {course.batches.map((batch) => (
                    <BatchMarkSheetTable
                      key={batch.batchId}
                      batch={batch}
                      markType={course.markType}
                      onStatusChange={handleStatusChange}
                      onRefetch={loadReport}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
